import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoginState } from '../../client-redux/login/reducer';
import { AppState } from '../../client-redux/reducers';
import { Group, Button, Grid, GridCol, Table, Container, Text } from '@mantine/core';
import {
  EquipmentDeviceStatusLevel,
  equipmentRemoteOperation,
  fetchEquipmentStatus,
  IEquipmentDetails,
  IRemoteOperation
} from '../../../libraries/equipment';
import { formatTransactionType } from '../../utils/script';
import { Language } from '../../languages/languageHandler';
import ModalComponent from '../modal';
import { formatDateTimeLabel } from '../../utils/date';
import DisplayField from '../display-field';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import { StatusType } from '../../../libraries/zps';
import './styles.scss';

interface ShowEquipmentProps {
  eid: number;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
}

export enum OPERATION {
  RESTART = 'RESTART',
  SHUTDOWN = 'SHUTDOWN',
  UNLOCK_ESCROW = 'UNLOCK_ESCROW',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
  IN_SERVICE = 'IN_SERVICE',
  OPEN_ESCROW = 'OPEN_ESCROW',
  CLOSE_ESCROW = 'CLOSE_ESCROW',
  VPN_CONNECT = 'VPN_CONNECT',
  VPN_DISCONNECT = 'VPN_DISCONNECT'
}

const ShowEquipment = ({ eid, openDetails, hideBtn, lang }: ShowEquipmentProps) => {
  const login = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const [message, setMessage] = useState<IEquipmentDetails | null>(null);
  const [selectedEid] = useState<number>(eid);
  const controller = useRef(new AbortController()).current;
  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (!selectedEid) return;

    fetchEquipmentStatus(selectedEid)
      .then(response => {
        if (!isZarphSuccessResponse(response)) {
          handleApiError(response, lang.GLOBAL_ERROR);
          return;
        }

        setMessage(response.data.status);
      })
      .catch(err => handleApiError(err, lang.GLOBAL_ERROR));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEid]);

  const handleRemoteOperation = async (eid: number, operation: OPERATION) => {
    const payload: IRemoteOperation = { eid, operation };

    try {
      const response = await equipmentRemoteOperation(controller, payload);

      if (!isZarphSuccessResponse(response)) {
        handleApiError(response, lang.GLOBAL_ERROR);
        return;
      }

      showStatusOperDialog({
        type: StatusType.INFO,
        message: lang.DEPOSIT_EQUIPMENTS_TRANSACTIONS_ALERT_COMMAND || 'Operation completed successfully.',
        statusOper: response.data.statusOper
      });
    } catch (err) {
      handleApiError(err, lang.GLOBAL_ERROR);
    }
  };

  const getEquipmentStatus = (status?: number) => {
    if (status === undefined) return lang.EQUIPMENT_STATUS_LABEL_NOT_DEFINED;
    switch (status) {
      case null:
        return lang.EQUIPMENT_NO_STATUS;
      case 0:
        return lang.EQUIPMENT_STATUS_LABEL_NOT_DEFINED;
      case 1:
        return lang.EQUIPMENT_STATUS_LABEL_DEFINED;
      case 2:
        return lang.EQUIPMENT_STATUS_LABEL_INICIALIZED;
      case 3:
        return lang.EQUIPMENT_STATUS_LABEL_ONLINE;
      case 4:
        return lang.EQUIPMENT_STATUS_LABEL_OFFLINE;
      default:
        return `UNKNOWN_STATE_${status}`;
    }
  };

  const getStatusLevelClass = (level: EquipmentDeviceStatusLevel) => {
    switch (level) {
      case EquipmentDeviceStatusLevel.OK:
        return 'status-ok';
      case EquipmentDeviceStatusLevel.WARNING:
        return 'status-warning';
      case EquipmentDeviceStatusLevel.ERROR:
        return 'status-error';
      default:
        return 'status-unknown';
    }
  };

  return (
    <>
      <ModalComponent
        show={openDetails}
        handleClick={hideBtn}
        title={`EID: ${selectedEid}`}
        classname="equipment-details"
        content={
          <Container classNames={{ root: 'show-equipment-details' }}>
            <Group>
              <Grid>
                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title="Status"
                    content={
                      lang && (
                        <div className={message?.status === 4 ? 'offlineStatus' : 'onlineStatus'}>
                          {message?.status ? getEquipmentStatus(message?.status) : `${lang.EQUIPMENT_NO_STATUS}`}
                        </div>
                      )
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_STATE}
                    content={
                      <div className={message?.inService === false ? 'notInService' : 'inService'}>
                        {message?.status
                          ? message.inService
                            ? `${lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE}`
                            : `${lang.EQUIPMENT_STATE_BUTTON_OUT_OF_SERVICE}`
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </div>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_LAST_COM_DT}
                    content={
                      <>
                        {message?.lastComDt
                          ? formatDateTimeLabel(message?.lastComDt, region)
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_LAST_OPER}
                    content={
                      <>
                        {message?.lastOperation
                          ? formatTransactionType(lang, message?.lastOperation)
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_LAST_OPER_DT}
                    content={
                      <>
                        {message?.lastOperationDt
                          ? formatDateTimeLabel(message?.lastOperationDt, region)
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.GLOBAL_IP} content={<>{message?.lastCommIp || 'N/A'}</>} />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.GLOBAL_IP_LOCAL} content={<>{message?.lastLocalIp || 'N/A'}</>} />
                </GridCol>
              </Grid>

              {message?.statusList && (
                <Group classNames={{ root: 'equipment-status-table' }}>
                  <Table.ScrollContainer minWidth={500}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {message.statusList.map((status, index) => (
                          <Table.Tr key={index}>
                            <Table.Th>{lang[status?.idLangKey]}</Table.Th>
                            <Table.Td className={getStatusLevelClass(status?.level)}>
                              {lang[status?.statusLangKey]}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Group>
              )}

              {(login.permissions.hasNonFinancialRemoteOperations() ||
                login.permissions.hasFinancialRemoteOperations()) && (
                <Group className="equip-details-buttons">
                  {login.permissions.hasNonFinancialRemoteOperations() && (
                    <>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.RESTART)}>
                        {lang.EQUIPMENT_STATE_BUTTON_RESTART}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.SHUTDOWN)}>
                        {lang.EQUIPMENT_STATE_BUTTON_SHUTDOWN}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.OUT_OF_SERVICE)}>
                        {lang.EQUIPMENT_STATE_BUTTON_OUT_OF_SERVICE}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.IN_SERVICE)}>
                        {lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.VPN_CONNECT)}>
                        VPN Connect
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.VPN_DISCONNECT)}>
                        VPN Disconnect
                      </Button>
                    </>
                  )}
                  {login.permissions.hasFinancialRemoteOperations() && message?.equipment?.etid === 412 && (
                    <>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.OPEN_ESCROW)}>
                        {lang.EQUIPMENT_STATE_BUTTON_OPEN_ESCROW}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.CLOSE_ESCROW)}>
                        {lang.EQUIPMENT_STATE_BUTTON_CLOSE_ESCROW}
                      </Button>
                    </>
                  )}
                </Group>
              )}
            </Group>

            {message?.softwareVersion && (
              <Group className="footer-version">
                <Text>{`${lang.EQUIPMENT_SOFTWARE_VERSION}: ${message.softwareVersion}`}</Text>
              </Group>
            )}
          </Container>
        }
      />
    </>
  );
};

export default ShowEquipment;
